/* ==========================================================================
   The console's spoken channel.

   Two regions, and the difference between them is the whole design:

     polite     the surface changed, the browser lost the network, the poll is
                held. Said when the reader next pauses.
     assertive  a robot entered fault. Said now, over whatever is being read,
                because it is the one class of event an operator must not miss.

   RULES
     · Nothing is announced that is not also on screen. This module reads
       rendered truth (the document title, a row that just gained data-alarm)
       and never invents a fact of its own.
     · Repeats are suppressed. A poll that re-renders the same surface every
       five seconds must not say its name every five seconds.
     · Assertive announcements coalesce inside a 3s window, so eight robots
       faulting at once is one sentence and not eight interruptions.
   ========================================================================== */

const COALESCE_MS = 3000;

let politeNode: HTMLElement | null = null;
let assertiveNode: HTMLElement | null = null;

let lastPolite = '';
let pending: string[] = [];
let timer: number | null = null;

function region(live: 'polite' | 'assertive'): HTMLElement {
  const el = document.createElement('p');
  el.setAttribute('aria-live', live);
  el.setAttribute('aria-atomic', 'true');
  if (live === 'assertive') el.setAttribute('role', 'alert');
  el.className = 'f3-sr-only';
  return el;
}

/** Called once by install(); the nodes live in the console layer's own root so
    nothing else can unmount them. */
export function mountAnnouncer(host: HTMLElement): void {
  if (politeNode) return;
  politeNode = region('polite');
  assertiveNode = region('assertive');
  host.append(politeNode, assertiveNode);
}

/**
 * Say something once. Identical consecutive polite messages are dropped.
 * `assertive` messages inside one 3s window are joined into a single sentence.
 */
export function announce(message: string, opts: { assertive?: boolean } = {}): void {
  const text = message.trim();
  if (text === '') return;

  if (opts.assertive !== true) {
    if (text === lastPolite) return;
    lastPolite = text;
    if (politeNode) politeNode.textContent = text;
    return;
  }

  /* The first one goes out now — an alert delayed by three seconds is not an
     alert. Anything that lands during the window that follows is held and
     spoken as one sentence when it closes. */
  if (timer === null) {
    say(text);
    timer = window.setTimeout(flush, COALESCE_MS);
    return;
  }
  pending.push(text);
}

function say(text: string): void {
  if (!assertiveNode) return;
  /* Clearing first forces a re-announcement when the text repeats — a second
     fault on the same robot is still news. */
  assertiveNode.textContent = '';
  assertiveNode.textContent = text;
}

function flush(): void {
  timer = null;
  const batch = pending;
  pending = [];
  if (batch.length === 0) return;
  say(batch.join('. '));
  timer = window.setTimeout(flush, COALESCE_MS);
}
